import React, {useEffect, useState} from "react";
import axios from "axios";
import {PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from 'recharts';
import { AlertTriangle, Clock, ChevronRight, CheckCircle } from 'lucide-react';
import BackOfficeMenu from "../components/BackOfficeMenu";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import {usePermissions} from "../../utils/usePermissions";
import "./Dashboard.css";

const API_URL = import.meta.env.VITE_API_URL;

// Couleurs de la charte graphique SASTRA Energy
const COLORS = {
    primaryBlue: '#1e3a5f',
    primaryBlueDark: '#0f1f2e',
    primaryYellow: '#ffd700',
    secondaryYellow: '#ffb700',
    accentOrange: '#ff8c00',
    green: '#00C49F',
    lightBlue: '#e8f4fd'
};

const CHART_COLORS = [COLORS.primaryBlue, COLORS.primaryYellow, COLORS.accentOrange, COLORS.green, COLORS.secondaryYellow, COLORS.primaryBlueDark, COLORS.lightBlue];

const Dashboard = ({onNavigate, onLogout, currentPage}) => {
    const {hasPermission, typeUser} = usePermissions();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('total');
    
    // Nouveaux filtres
    const [mois, setMois] = useState('');
    const [annee, setAnnee] = useState('');
    const [categorie, setCategorie] = useState('');

    const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const CATEGORIES = ['Région de Tunis', 'Région Nord', 'Région Nord-Ouest', 'Région Centre', 'Région Sfax', 'Région Sud-Ouest', 'Région Sud'];
    const YEARS = [2024, 2025, 2026, 2027];

    useEffect(() => {
        if (!typeUser) return;

        if (typeUser !== "admin" && typeUser !== "moderateur") {
            onNavigate("profile");
            return;
        }

        // Si c'est un modérateur, vérifier explicitement la permission 'dashboard:read'
        if (typeUser === "moderateur" && !hasPermission("dashboard", "read")) {
            onNavigate("profile");
            return;
        }
    }, [onNavigate, typeUser, hasPermission]);

    useEffect(() => {
        fetchStats();
    }, [mois, annee, categorie]);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/dashboard/all-stats`, {
                headers: {Authorization: `Bearer ${token}`},
                params: { mois, annee, categorie }
            });

            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Erreur chargement statistiques:', error);
        } finally {
            setLoading(false);
        }
    };

    // ✨ Animations anime.js après le chargement des données
    useEffect(() => {
        if (loading || !stats || typeof window.anime !== 'function') return;

        // Petit délai pour s'assurer que le DOM est prêt
        setTimeout(() => {
            // Animation du header
            window.anime({
                targets: '.dashboard-header',
                opacity: [0, 1],
                translateY: [-30, 0],
                duration: 800,
                easing: 'easeOutExpo',
                delay: 100
            });

            // Animation des cartes de stats
            window.anime({
                targets: '.stat-card',
                opacity: [0, 1],
                scale: [0.8, 1],
                duration: 600,
                easing: 'easeOutBack',
                delay: window.anime.stagger(100, {start: 300})
            });

            // Animation des graphiques
            window.anime({
                targets: '.chart-card',
                opacity: [0, 1],
                translateY: [40, 0],
                duration: 800,
                easing: 'easeOutQuad',
                delay: window.anime.stagger(150, {start: 600})
            });

            // Animation des statistiques supplémentaires
            window.anime({
                targets: '.stat-box',
                opacity: [0, 1],
                scale: [0.9, 1],
                duration: 600,
                easing: 'easeOutElastic(1, .6)',
                delay: window.anime.stagger(100, {start: 1000})
            });

            // Animation flottante des valeurs de stats
            window.anime({
                targets: '.stat-value',
                translateY: [-5, 5],
                duration: 2000,
                easing: 'easeInOutSine',
                direction: 'alternate',
                loop: true,
                delay: window.anime.stagger(300)
            });
        }, 100);
    }, [loading, stats]);

    const getVisitorCount = () => {
        if (!stats) return 0;
        switch (period) {
            case 'today': return stats.visitors.today;
            case 'month': return stats.visitors.month;
            case 'year': return stats.visitors.year;
            case 'total': return stats.visitors.total;
            default: return stats.visitors.total;
        }
    };

    const getVisitorsChartData = () => {
        if (!stats) return [];
        switch (period) {
            case 'today': return stats.visitors.last7Days || [];
            case 'month': return stats.visitors.last4Weeks || [];
            case 'year': return stats.visitors.last12Months || [];
            case 'total': return stats.visitors.last12Months || [];
            default: return stats.visitors.last12Months || [];
        }
    };

    const getVisitorsChartTitle = () => {
        switch (period) {
            case 'today': return 'Évolution des Visiteurs (7 derniers jours)';
            case 'month': return 'Évolution des Visiteurs (4 dernières semaines)';
            case 'year': return 'Évolution des Visiteurs (12 derniers mois)';
            case 'total': return 'Évolution des Visiteurs (Total)';
            default: return 'Évolution des Visiteurs';
        }
    };

    const getRegionData = () => {
        if (!stats) return [];
        switch (period) {
            case 'today': return stats.regions.today;
            case 'month': return stats.regions.month;
            case 'year': return stats.regions.year;
            case 'total': return stats.regions.year; // Fallback to year for regions if total is not mapped
            default: return stats.regions.year;
        }
    };

    if (loading) {
        return (
            <div className="dashboard-container">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dashboard-content">
                    <LoadingSpinner message="Chargement des statistiques..." />
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="dashboard-container">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dashboard-content">
                    <div className="error-message">Erreur de chargement des données</div>
                </div>
            </div>
        );
    }

    // Préparer les données pour le graphique des devis (Pie Chart)
    const devisData = [
        {name: 'Validés', value: stats.devis.valides, color: COLORS.green},
        {name: 'En cours', value: stats.devis.enCours, color: '#17a2b8'},
        {name: 'En attente', value: stats.devis.enAttente, color: COLORS.primaryYellow},
        {name: 'Rejetés', value: stats.devis.rejetes, color: COLORS.accentOrange}
    ];

    // Préparer les données pour le graphique des réclamations (Pie Chart)
    const reclamationsData = [
        {name: 'Résolues', value: stats.reclamations.resolues, color: COLORS.green},
        {name: 'En cours', value: stats.reclamations.enCours, color: COLORS.primaryBlue},
        {name: 'En attente', value: stats.reclamations.enAttente, color: COLORS.primaryYellow}
    ];

    // Données pour le graphique d'âge
    const ageChartData = stats.clients.byAge || [];

    // Données pour le graphique des régions (clients)
    const regionClientsData = stats.clients.byRegion || [];

    return (
        <div className="dashboard-container">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="dashboard-content">
                <div className="dashboard-header">
                    <div>
                        <h1>Tableau de Bord</h1>
                        <p>Vue d'ensemble des statistiques</p>
                    </div>
                    <button className="refresh-btn" onClick={fetchStats}>
                        Actualiser
                    </button>
                </div>

                

                {/* 🚨 Carte Opérationnelle : Dossiers Bloqués chez Sastra */}
                {stats.sastraBloquesCard && (
                    <div className="sastra-bloques-card-container" style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '14px',
                        padding: '24px',
                        marginTop: '24px',
                        marginBottom: '24px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: stats.sastraBloquesCard.total > 0 ? '#fef3c7' : '#dcfce7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: stats.sastraBloquesCard.total > 0 ? '#d97706' : '#15803d'
                                }}>
                                    {stats.sastraBloquesCard.total > 0 ? <AlertTriangle size={20} /> : <CheckCircle size={20} />}
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
                                        Dossiers bloqués chez Sastra
                                    </h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                                        {stats.sastraBloquesCard.total > 0
                                            ? `${stats.sastraBloquesCard.total} dossier${stats.sastraBloquesCard.total > 1 ? 's nécessitent' : ' nécessite'} une vérification (déposé ≥ 6 jours)`
                                            : 'Tous les dossiers déposés depuis plus de 6 jours ont été traités'}
                                    </p>
                                </div>
                            </div>
                            {stats.sastraBloquesCard.total > 0 && stats.sastraBloquesCard.maxDays > 0 && (
                                <div style={{
                                    backgroundColor: '#fff7ed',
                                    border: '1px solid #ffedd5',
                                    color: '#c2410c',
                                    padding: '6px 14px',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <Clock size={14} />
                                    <span>Délai maximum : {stats.sastraBloquesCard.maxDays} jours</span>
                                </div>
                            )}
                        </div>

                        {stats.sastraBloquesCard.total > 0 ? (
                            <>
                                {/* Tranches d'ancienneté */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                                    <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', padding: '12px 16px', borderRadius: '10px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#b45309', textTransform: 'uppercase' }}>6–10 jours</div>
                                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#d97706', marginTop: '2px' }}>{stats.sastraBloquesCard.count6to10}</div>
                                        <span style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: 600 }}>Vigilance</span>
                                    </div>
                                    <div style={{ backgroundColor: '#fff7ed', border: '1px solid #ffedd5', padding: '12px 16px', borderRadius: '10px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#c2410c', textTransform: 'uppercase' }}>11–20 jours</div>
                                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ea580c', marginTop: '2px' }}>{stats.sastraBloquesCard.count11to20}</div>
                                        <span style={{ fontSize: '0.7rem', color: '#ea580c', fontWeight: 600 }}>Important</span>
                                    </div>
                                    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fee2e2', padding: '12px 16px', borderRadius: '10px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#b91c1c', textTransform: 'uppercase' }}>+20 jours</div>
                                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#dc2626', marginTop: '2px' }}>{stats.sastraBloquesCard.count20Plus}</div>
                                        <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>Critique</span>
                                    </div>
                                </div>

                                {/* Liste des 5 dossiers les plus anciens */}
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', marginBottom: '10px' }}>
                                        Dossiers les plus anciens en attente (Top 5) :
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {(stats.sastraBloquesCard.top5 || []).map((item, idx) => {
                                            const badgeBg = item.diffDays > 20 ? '#fee2e2' : item.diffDays >= 11 ? '#ffedd5' : '#fef3c7';
                                            const badgeColor = item.diffDays > 20 ? '#dc2626' : item.diffDays >= 11 ? '#c2410c' : '#b45309';
                                            return (
                                                <div key={idx} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '10px 14px',
                                                    backgroundColor: '#f8fafc',
                                                    borderRadius: '8px',
                                                    border: '1px solid #f1f5f9',
                                                    flexWrap: 'wrap',
                                                    gap: '8px'
                                                }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{item.clientName}</span>
                                                        <div style={{ display: 'flex', gap: '12px', fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                                                            <span>Réf : <code style={{ backgroundColor: '#e2e8f0', padding: '1px 5px', borderRadius: '4px', color: '#334155' }}>{item.referenceSteg}</code></span>
                                                            <span>Déposé le {new Date(item.dateDepot).toLocaleDateString('fr-FR')}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{
                                                            padding: '4px 10px',
                                                            borderRadius: '20px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            backgroundColor: badgeBg,
                                                            color: badgeColor
                                                        }}>
                                                            {item.diffDays} jours
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => onNavigate('detail-dossier', { clientId: item.clientId, dossierId: item.dossierId })}
                                                            style={{
                                                                padding: '4px 10px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                color: '#1e3a5f',
                                                                backgroundColor: '#ffffff',
                                                                border: '1px solid #cbd5e1',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            Voir dossier
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Bouton Voir tous */}
                                <div style={{ textAlign: 'center', paddingTop: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => onNavigate('dashboard-client', { tab: 'bloques' })}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '8px 18px',
                                            fontSize: '0.85rem',
                                            fontWeight: 700,
                                            color: '#ffffff',
                                            backgroundColor: '#1e3a5f',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 6px rgba(30, 58, 95, 0.25)',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <span>Voir tous les dossiers bloqués</span>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '24px 12px',
                                backgroundColor: '#f0fdf4',
                                borderRadius: '10px',
                                border: '1px solid #bbf7d0',
                                color: '#166534'
                            }}>
                                <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>
                                    Aucun dossier bloqué chez Sastra
                                </div>
                                <div style={{ fontSize: '0.82rem', color: '#15803d' }}>
                                    Tous les dossiers déposés depuis plus de 6 jours ont été traités.
                                </div>
                            </div>
                        )}
                    </div>
                )}
{/* Cartes de statistiques principales */}
                <div className="stats-cards">
                    <div className="stat-card blue">
                        <div className="stat-value">{getVisitorCount()}</div>
                        <div className="stat-label">Visiteurs</div>
                        <div className="stat-period">
                            {period === 'today' ? "Aujourd'hui" : period === 'month' ? "Ce mois" : period === 'year' ? "Cette année" : "Total"}
                        </div>
                    </div>

                    <div className="stat-card green">
                        <div className="stat-value">{stats.clients.total}</div>
                        <div className="stat-label">Clients Total</div>
                        <div className="stat-period">+{stats.clients.newThisMonth} ce mois</div>
                    </div>

                    <div className="stat-card orange">
                        <div className="stat-value">{stats.devis.total}</div>
                        <div className="stat-label">Devis</div>
                        <div className="stat-period">{stats.devis.valides} validés</div>
                    </div>
                    <div className="stat-card purple">
                        <div className="stat-value">{stats.reclamations.total}</div>
                        <div className="stat-label">Réclamations</div>
                        <div className="stat-period">{stats.reclamations.resolues} résolues</div>
                    </div>

                    <div
                        className="stat-card red"
                        style={{
                            cursor: 'pointer',
                            position: 'relative',
                            borderLeft: '4px solid #ef4444',
                            backgroundColor: stats.sastraBloquesCard?.total > 0 ? '#fff5f5' : '#ffffff'
                        }}
                        onClick={() => onNavigate('dashboard-client', { tab: 'bloques' })}
                        title="Cliquer pour voir la liste des dossiers bloqués chez Sastra"
                    >
                        <div className="stat-value" style={{ color: stats.sastraBloquesCard?.total > 0 ? '#dc2626' : '#1e293b' }}>
                            {stats.sastraBloquesCard ? stats.sastraBloquesCard.total : 0}
                        </div>
                        <div className="stat-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>Bloqués Sastra</span>
                            <AlertTriangle size={16} color={stats.sastraBloquesCard?.total > 0 ? '#dc2626' : '#64748b'} />
                        </div>
                        <div className="stat-period" style={{ color: stats.sastraBloquesCard?.total > 0 ? '#b91c1c' : '#6c757d' }}>
                            {stats.sastraBloquesCard?.total > 0
                                ? `≥ 6 j (Max : ${stats.sastraBloquesCard.maxDays} j)`
                                : 'Aucun dossier (≥ 6j)'}
                        </div>
                    </div>
                </div>
                {/* 📊 Section Analyse de la Puissance Installée (Groupée) */}
                <div className="power-analysis-container">
                    <h2 className="section-title">Analyse de la Puissance Installée</h2>
                    
                    {/* Filtres Avancés */}
                    <div className="dashboard-filters-row">
                        <div className="filter-group">
                            <label>Mois</label>
                            <select value={mois} onChange={(e) => setMois(e.target.value)}>
                                <option value="">Tous les mois</option>
                                {MOIS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Année</label>
                            <select value={annee} onChange={(e) => setAnnee(e.target.value)}>
                                <option value="">Toutes les années</option>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Région STEG</label>
                            <select value={categorie} onChange={(e) => setCategorie(e.target.value)}>
                                <option value="">Toutes les régions</option>
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <button className="clear-filters-btn" onClick={() => {setMois(''); setAnnee(''); setCategorie('');}}>
                            Effacer
                        </button>
                    </div>

                    {/* Cartes de Puissance */}
                    <div className="power-stats-grid">
                        <div className="stat-box global total-power">
                            <h4>Puissance Totale</h4>
                            <div className="stat-big">{(stats.clients?.power?.total || 0).toFixed(2)} kWc</div>
                            <p>{stats.clients.total} dossiers au total</p>
                        </div>
                        <div className="stat-box bt">
                            <h4>Puissance BT</h4>
                            <div className="stat-big">{(stats.clients?.power?.bt || 0).toFixed(2)} kWc</div>
                            <p>{stats.clients?.power?.countBT || 0} dossiers BT</p>
                        </div>
                        <div className="stat-box mt">
                            <h4>Puissance MT</h4>
                            <div className="stat-big">{(stats.clients?.power?.mt || 0).toFixed(2)} kWc</div>
                            <p>{stats.clients?.power?.countMT || 0} dossiers MT</p>
                        </div>
                        <div className="stat-box global">
                            <h4>Moyenne BT</h4>
                            <div className="stat-big">
                                {stats.clients.power.countBT > 0 
                                    ? (stats.clients.power.bt / stats.clients.power.countBT).toFixed(2) 
                                    : 0} kWc
                            </div>
                            <p>Par dossier BT</p>
                        </div>
                        <div className="stat-box global mt-avg">
                            <h4>Moyenne MT</h4>
                            <div className="stat-big">
                                {stats.clients.power.countMT > 0 
                                    ? (stats.clients.power.mt / stats.clients.power.countMT).toFixed(2) 
                                    : 0} kWc
                            </div>
                            <p>Par dossier MT</p>
                        </div>
                    </div>
                </div>

                {/* Sélecteur de période */}
                <div className="period-selector">
                    <button
                        className={period === 'total' ? 'active' : ''}
                        onClick={() => setPeriod('total')}
                    >
                        Tout
                    </button>
                    <button
                        className={period === 'today' ? 'active' : ''}
                        onClick={() => setPeriod('today')}
                    >
                        Aujourd'hui
                    </button>
                    <button
                        className={period === 'month' ? 'active' : ''}
                        onClick={() => setPeriod('month')}
                    >
                        Ce Mois
                    </button>
                    <button
                        className={period === 'year' ? 'active' : ''}
                        onClick={() => setPeriod('year')}
                    >
                        Cette Année
                    </button>
                </div>

                {/* Graphiques en 2 colonnes */}
                <div className="charts-grid">
                    {/* Graphique Devis - Pie Chart */}
                    <div className="chart-card">
                        <h3>Statut des Devis</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={devisData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {devisData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="chart-legend">
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: COLORS.green}}></span>
                                Validés: {stats.devis.valides}
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: '#17a2b8'}}></span>
                                En cours: {stats.devis.enCours}
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: COLORS.primaryYellow}}></span>
                                En attente: {stats.devis.enAttente}
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: COLORS.accentOrange}}></span>
                                Rejetés: {stats.devis.rejetes}
                            </div>
                        </div>
                    </div>

                    {/* Graphique Réclamations - Pie Chart */}
                    <div className="chart-card">
                        <h3>Statut des Réclamations</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={reclamationsData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {reclamationsData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="chart-legend">
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: COLORS.green}}></span>
                                Résolues: {stats.reclamations.resolues}
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: COLORS.primaryBlue}}></span>
                                En cours: {stats.reclamations.enCours}
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot" style={{backgroundColor: COLORS.primaryYellow}}></span>
                                En attente: {stats.reclamations.enAttente}
                            </div>
                        </div>
                    </div>

                    {/* Graphique Clients par Âge - Bar Chart */}
                    <div className="chart-card">
                        <h3>Clients par Tranche d'Âge</h3>
                        {ageChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={ageChartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="age" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill={COLORS.primaryBlue} name="Nombre de clients">
                                        {ageChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999'}}>
                                Aucune donnée disponible. Les clients n'ont pas renseigné leur âge.
                            </div>
                        )}
                    </div>

                    {/* Graphique Clients par Région - Bar Chart */}
                    <div className="chart-card">
                        <h3>Clients par Région (Top 10)</h3>
                        {regionClientsData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={regionClientsData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="region" angle={-45} textAnchor="end" height={100} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill={COLORS.primaryYellow} name="Nombre de clients" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999'}}>
                                Aucune donnée disponible. Les clients n'ont pas renseigné leur région.
                            </div>
                        )}
                    </div>

                    {/* Graphique Visiteurs (7 derniers jours) - Bar Chart */}
                    <div className="chart-card">
                        <h3>{getVisitorsChartTitle()}</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={getVisitorsChartData()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="visitors" fill={COLORS.primaryBlue} name="Visiteurs" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Graphique Devis (6 derniers mois) - Bar Chart */}
                    <div className="chart-card">
                        <h3>Évolution des Devis (6 derniers mois)</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={stats.devis.last6Months}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="devis" fill={COLORS.accentOrange} name="Devis" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Statistiques supplémentaires */}
                <div className="additional-stats">
                    <div className="stat-box">
                        <h4>Références</h4>
                        <div className="stat-big">{stats.references.total}</div>
                        <p>Projets réalisés</p>
                    </div>
                    <div className="stat-box">
                        <h4>Produits</h4>
                        <div className="stat-big">{stats.produits.total}</div>
                        <p>Dans le catalogue</p>
                    </div>
                    <div className="stat-box">
                        <h4>Taux de validation</h4>
                        <div className="stat-big">
                            {stats.devis.total > 0 ? Math.round((stats.devis.valides / stats.devis.total) * 100) : 0}%
                        </div>
                        <p>Des devis</p>
                    </div>
                    <div className="stat-box">
                        <h4>Taux de résolution</h4>
                        <div className="stat-big">
                            {stats.reclamations.total > 0 ? Math.round((stats.reclamations.resolues / stats.reclamations.total) * 100) : 0}%
                        </div>
                        <p>Des réclamations</p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Dashboard;
